import patientRequest from "@/api/patient-request";

/**
 * Types the browser can show inline without running script. Blob URLs share the portal's
 * origin, so HTML/SVG and anything unknown are downloaded instead of rendered.
 */
const VIEWABLE_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  txt: "text/plain",
};

export function viewTypeFor(fileName: string | undefined): string | undefined {
  return VIEWABLE_TYPES[fileName?.split(".").pop()?.toLowerCase() ?? ""];
}

function base64ToBlob(b64: string, type: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

/**
 * Fetches a portal-shared file and shows it in a new tab, or downloads it when the type
 * can't be shown safely. Call it straight from a click handler: the tab is opened before
 * the request so popup blockers (iOS Safari especially) allow it. Throws on failure.
 *
 * @param expectedName file name known up front (decides whether to open a tab at all)
 */
export async function openPortalFile(params: {
  facilityId: number;
  fileId: number;
  expectedName?: string;
}): Promise<void> {
  const { facilityId, fileId, expectedName } = params;
  const win = viewTypeFor(expectedName) ? window.open("", "_blank") : null;
  try {
    const res = await patientRequest.request.downloadfilebase64({
      facilityId: String(facilityId),
      id: String(fileId),
    });
    const data = res?.data as { base64?: string; originalFileName?: string } | null;
    if (!data?.base64) throw new Error("Empty file");

    const fileName = data.originalFileName || expectedName || "document";
    const viewType = viewTypeFor(fileName);
    const url = URL.createObjectURL(
      base64ToBlob(data.base64, viewType ?? "application/octet-stream")
    );
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);

    if (viewType && win) {
      win.opener = null;
      win.location.href = url;
    } else {
      win?.close();
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
    }
  } catch (err) {
    win?.close();
    throw err;
  }
}
